import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { client, unwrap } from "./client";
import type { Features } from "./types";

/**
 * Phase 5 (P1a §11): `GET /api/v1/meta/features` is anonymous and reachable before a caller has a session, an
 * operator key, or even an account — a web client uses it to decide whether to show a login form, a register
 * form, or both. Read once on boot, here, rather than per-screen: every consumer (the router's redirect target,
 * the register link, the practice nav, the forgot-password mail hint) needs the same three booleans and none of
 * them may block first paint on it, so the defaults below are the CONSERVATIVE ones (single-user, no practice,
 * no mail) — a slow or failed fetch never offers a form the deployment did not ask for.
 */
const DEFAULT_FEATURES: Features = {
  practice_enabled: false,
  multiuser_enabled: false,
  mail_configured: false,
  signup_restricted: false,
  // xmg-mfa-switch: conservative = enforced; only an explicit `false` from the server lifts the gate.
  mfa_enforced: true,
};

export type FeaturesState = {
  features: Features;
  /** True only until the first response (or failure) lands; never true again after that. */
  loading: boolean;
};

/**
 * Defaults to the conservative flags rather than `null` + throw (unlike `useSession`'s contract): a screen
 * consuming `useFeatures()` for a nice-to-have (hide the register link, skip the practice nav) must not crash
 * every existing test file that renders RequireSession or RootLayout with no FeaturesProvider anywhere in the
 * tree — of which there are eleven, none of them P1-web's to touch. A real deployment always gets the provider
 * from App.tsx; a test that cares what the flags say adds FeaturesProvider (or a server.use(...) handler)
 * explicitly, exactly like any other screen test does for its own data.
 */
const FeaturesContext = createContext<FeaturesState>({ features: DEFAULT_FEATURES, loading: false });

export function FeaturesProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [features, setFeatures] = useState<Features>(DEFAULT_FEATURES);
  const [loading, setLoading] = useState(true);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    void (async () => {
      try {
        const data = await unwrap(client.GET("/api/v1/meta/features", {}));
        if (mounted.current) setFeatures(data as Features);
      } catch {
        // Anonymous and best-effort: a network failure keeps the conservative defaults rather than surfacing
        // an error screen before the app has even decided which login form to show.
      } finally {
        if (mounted.current) setLoading(false);
      }
    })();
    return () => {
      mounted.current = false;
    };
  }, []);

  const value = useMemo<FeaturesState>(() => ({ features, loading }), [features, loading]);
  return <FeaturesContext.Provider value={value}>{children}</FeaturesContext.Provider>;
}

export function useFeatures(): FeaturesState {
  return useContext(FeaturesContext);
}
